import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Typography,
  DatePicker,
  message,
  Row,
  Col, Radio
} from 'antd';
import styled from 'styled-components';
import PersistentDrawerLeft from '../../../components/reusable/admin/Main';
import { restApi } from '../../../constants/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const StyledContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 20px;
  background-color: #f0f2f5;
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 950px;
  border-radius: 16px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  padding: 30px;
`;

const StyledButton = styled(Button)`
  background-color: #1677ff;
  border-radius: 8px;
  font-size: 16px;
  height: 40px;
  width: 160px;

  &:hover {
    background-color: #0056d2;
  }
`;

const FormLabel = styled.div`
  font-weight: 500;
`;

const UserCreate = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [userTypes, setUserTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchUserTypes = async () => {
    try {
      const res: any = await restApi('/api/user-types', 'GET');
      setUserTypes(res?.data || []);
    } catch (err) {
      console.error(err);
      message.error('Failed to fetch Designations');
    }
  };
  const fetchDepartment = async () => {
    try {
      const res: any = await restApi('/api/departments', 'GET');
      setDepartments(res?.data || []);
    } catch (err) {
      console.error(err);
      message.error('Failed to fetch Designations');
    }
  };

  const fetchUserById = async () => {
    try {
      const res: any = await restApi(`/api/users/user_by_id/${id}`, 'GET');
      const user = res?.data;
      form.setFieldsValue({
        ...user,
        date_of_joining: user.date_of_joining ? dayjs(user.date_of_joining) : null,
        date_of_review: user.date_of_review ? dayjs(user.date_of_review) : null,
        date_of_end: user.date_of_end ? dayjs(user.date_of_end) : null,
      });
    } catch (err) {
      console.error(err);
      message.error('Failed to fetch user data');
    }
  };

  useEffect(() => {
    fetchUserTypes();
    fetchDepartment()
    if (id) fetchUserById();
  }, [id]);

  const onFinish = async (values: any) => {
    setLoading(true);
    const formattedValues = {
      ...values,
      date_of_joining: values.date_of_joining?.format('YYYY-MM-DD') || null,
      date_of_review: values.date_of_review?.format('YYYY-MM-DD') || null,
      date_of_end: values.date_of_end?.format('YYYY-MM-DD') || null,
    };

    try {
      if (id) {
        await restApi(`/api/users/update/${id}`, 'POST', formattedValues);
        message.success('User updated successfully');
      } else {
        await restApi('/api/users/create', 'POST', formattedValues);
        message.success('User created successfully');
        form.resetFields();
      }
      navigate('/admin/users');
      setLoading(false);
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || 'Something went wrong';
      console.error(errMsg);
      message.error(errMsg);
      setLoading(false);
    }
  };

  return (
    <PersistentDrawerLeft>
      <StyledContainer>
        <StyledCard>
          <Title level={3} style={{ textAlign: 'center', marginBottom: 40, marginTop: 0 }}>
            {id ? 'Update User' : 'Create User'}
          </Title>

          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            autoComplete="off"
          >
            <Row gutter={24}>
              <Col xs={24} md={12} lg={3} xl={3}>
                <Form.Item
                  label={<FormLabel>Prefix</FormLabel>}
                  name="prefixName"
                  rules={[{ required: true, message: 'Please select prefix' }]}
                >
                  <Select size="middle" placeholder="Select prefix">
                    <Option value="Mr">Mr</Option>
                    <Option value="Mrs">Mrs</Option>
                    <Option value="Miss">Miss</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={14} xl={14}>
                <Form.Item
                  label={<FormLabel>Name</FormLabel>}
                  name="name"
                  rules={[{ required: true, message: 'Please enter name' }]}
                >
                  <Input size="middle" placeholder="Enter full name" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={7} xl={7}>
                <Form.Item
                  label={<FormLabel>Mobile Number</FormLabel>}
                  name="mobileNo"
                  rules={[
                    { required: true, message: 'Please enter mobile number' },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: 'Mobile number must be exactly 10 digits',
                    },
                  ]}
                >
                  <Input
                    size="middle"
                    placeholder="Enter mobile number"
                    type="number"
                    maxLength={10} // prevent more than 10 digits input
                  />
                </Form.Item>

              </Col>

              <Col xs={24} md={12} lg={9} xl={9}>
                <Form.Item label={<FormLabel>Email</FormLabel>} name="email">
                  <Input size="middle" placeholder="Enter email (optional)" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={10} xl={7}>
                <Form.Item
                  label={<FormLabel>Designation</FormLabel>}
                  name="user_type_id"
                  rules={[{ required: true, message: 'Please select Designation' }]}
                >
                  <Select size="middle" placeholder="Select Designation">
                    {userTypes.map((type: any) => (
                      <Option key={type.id} value={type.id}>
                        {type.type_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {
                localStorage.getItem("role") === "SUPERADMIN" && (
                  <Col xs={24} md={12} lg={10} xl={8}>
                    <Form.Item
                      label={<FormLabel>Department</FormLabel>}
                      name="departmentId"
                      rules={[{ required: true, message: 'Please select Department' }]}
                    >
                      <Select size="middle" placeholder="Select Department">
                        {departments.map((type: any) => (
                          <Option key={type.id} value={type.id}>
                            {type.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )
              }


              <Col xs={24} md={12} lg={5} xl={5}>
                <Form.Item
                  label={<FormLabel>Gender</FormLabel>}
                  name="gender"
                  rules={[{ required: true, message: 'Please select gender' }]}
                >
                  <Radio.Group>
                    <Radio value="Male">M</Radio>
                    <Radio value="Female">F</Radio>
                    <Radio value="Other">O</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={8} xl={8}>
                <Form.Item label={<FormLabel>Designation Code</FormLabel>} name="designation_code">
                  <Input size="middle" placeholder="Enter designation code" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={8} xl={8}>
                <Form.Item label={<FormLabel>Aadhar Number</FormLabel>} name="adharnumber">
                  <Input size="middle" type="number" placeholder="Enter Aadhar number" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={8} xl={8}>
                <Form.Item label={<FormLabel>PAN Number</FormLabel>} name="pannumber">
                  <Input size="middle" placeholder="Enter PAN number" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} xl={8}>
                <Form.Item label={<FormLabel>Bank Name</FormLabel>} name="bankName">
                  <Input size="middle" placeholder="Enter Bank Name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} xl={8}>
                <Form.Item label={<FormLabel>Branch IFSC Code</FormLabel>} name="branchIfscCode">
                  <Input size="middle" placeholder="Enter Branch IFSC Code" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} xl={8}>
                <Form.Item label={<FormLabel>Account Number</FormLabel>} name="accountNumber">
                  <Input size="middle" placeholder="Account Number" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={6} xl={6}>
                <Form.Item label={<FormLabel>Police Station</FormLabel>} name="police_station">
                  <Input size="middle" placeholder="Enter police station" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={6} xl={6}>
                <Form.Item label={<FormLabel>Date of Joining</FormLabel>} name="date_of_joining">
                  <DatePicker size="middle" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={6} xl={6}>
                <Form.Item label={<FormLabel>Date of Review</FormLabel>} name="date_of_review">
                  <DatePicker size="middle" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={6} xl={6}>
                <Form.Item label={<FormLabel>Date of End</FormLabel>} name="date_of_end">
                  <DatePicker size="middle" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label={<FormLabel>Remark of Incharge</FormLabel>} name="remark_of_incharge">
                  <Input.TextArea rows={3} placeholder="Enter remark of incharge" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item label={<FormLabel>Remark of OIC</FormLabel>} name="remark_of_oic">
                  <Input.TextArea rows={3} placeholder="Enter remark of OIC" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<FormLabel>Address</FormLabel>}
                  name="address"
                  rules={[{ required: true, message: 'Please enter address' }]}
                >
                  <Input.TextArea rows={3} placeholder="Enter full address" />
                </Form.Item>
              </Col>

            </Row>

            <Row justify="space-between" style={{ marginTop: 24 }}>
              <Col>
                <Button onClick={() => navigate('/admin/users')} size="middle">
                  Cancel
                </Button>
              </Col>
              <Col>
                <StyledButton type="primary" htmlType="submit" size="middle" loading={loading} disabled={loading}>
                  {/* {id ? 'Update User' : 'Create User'} */}
                  {id ? 'Updating' : loading ? 'Creating...' : 'Create User'}
                </StyledButton>
              </Col>
            </Row>
          </Form>
        </StyledCard>
      </StyledContainer>
    </PersistentDrawerLeft>
  );
};

export default UserCreate;
